import { CLIENT_SEED_MAX_LENGTH } from '@casino/shared';
import { ApiError, apiCall } from '../api/client.js';
import { audio } from '../audio/AudioManager.js';
import { applyUserState, store } from '../state/store.js';
import { enter } from './anim.js';
import { createSheet, el, type Sheet } from './dom.js';
import { toast } from './toast.js';

/**
 * Panneau « Équité » (provably fair).
 *
 * Le principe tient en trois valeurs : le serveur s'engage a l'avance sur une
 * graine en n'en publiant que l'empreinte (`serverSeedHash`), le joueur choisit
 * sa propre graine (`clientSeed`), et chaque tirage consomme un compteur
 * (`nonce`). Le client ne calcule rien ici : il affiche ce que le serveur
 * expose et relaie deux actions.
 */

let openSheet: Sheet | null = null;

function copyRow(label: string, value: string, hint: string): HTMLElement {
  const valueNode = el('code', { class: 'seed__value', text: value });

  const copyButton = el('button', {
    class: 'button button--quiet',
    text: 'Copier',
    attrs: { type: 'button' },
    on: {
      click: () => {
        void navigator.clipboard
          ?.writeText(value)
          .then(() => toast('Copié.', { kind: 'info', sound: false }))
          .catch(() => toast('Copie impossible sur cette plateforme.', { kind: 'error' }));
        audio.play('click');
      },
    },
  });

  return el('div', { class: 'seed' }, [
    el('div', { class: 'seed__head' }, [
      el('span', { class: 'seed__label', text: label }),
      copyButton,
    ]),
    valueNode,
    el('p', { class: 'seed__hint', text: hint }),
  ]);
}

export function openFairness(): void {
  if (openSheet) return;

  const user = store.state.user;
  if (!user) {
    toast('Profil pas encore chargé.', { kind: 'error' });
    return;
  }

  const sheet = createSheet({
    title: 'Équité',
    subtitle: 'Vérifiable : le serveur s’engage avant que tu joues.',
    onClose: () => {
      openSheet = null;
    },
  });
  openSheet = sheet;

  const nonceNode = el('span', { class: 'stat__value', text: String(user.nonce) });
  const hashHost = el('div', { class: 'stack stack--tight' }, [
    copyRow(
      'Empreinte de la graine serveur',
      user.serverSeedHash,
      'sha256 de la graine que le serveur utilise en ce moment. Elle est publiée avant tes tirages : le serveur ne peut plus changer d’avis.',
    ),
  ]);

  const revealHost = el('div', { class: 'stack stack--tight' });

  // --- graine client ------------------------------------------------------
  const seedInput = el('input', {
    class: 'input',
    attrs: {
      type: 'text',
      value: user.clientSeed,
      maxlength: String(CLIENT_SEED_MAX_LENGTH),
      spellcheck: 'false',
      autocomplete: 'off',
      'aria-label': 'Graine client',
    },
  });

  const saveButton = el('button', {
    class: 'button button--primary',
    text: 'Enregistrer',
    attrs: { type: 'button' },
  });

  saveButton.addEventListener('click', () => {
    const value = seedInput.value.trim();
    if (value.length === 0) {
      toast('La graine client ne peut pas être vide.', { kind: 'error' });
      return;
    }
    void submitClientSeed(value);
  });

  async function submitClientSeed(clientSeed: string): Promise<void> {
    saveButton.disabled = true;
    saveButton.textContent = 'Envoi…';
    try {
      const response = await apiCall('/api/seed/client', { clientSeed });
      applyUserState(response.user);
      seedInput.value = response.user.clientSeed;
      nonceNode.textContent = String(response.user.nonce);
      audio.play('click');
      toast('Graine client mise à jour.', { kind: 'success', sound: false });
    } catch (error) {
      toast(error instanceof ApiError ? error.userMessage : 'Échec de la mise à jour.', {
        kind: 'error',
      });
    } finally {
      saveButton.disabled = false;
      saveButton.textContent = 'Enregistrer';
    }
  }

  // --- rotation -----------------------------------------------------------
  const rotateButton = el('button', {
    class: 'button button--brass',
    text: 'Changer de graine',
    attrs: { type: 'button' },
  });

  rotateButton.addEventListener('click', () => void rotate());

  async function rotate(): Promise<void> {
    rotateButton.disabled = true;
    rotateButton.textContent = 'Rotation…';
    try {
      const response = await apiCall('/api/seed/rotate', {});
      applyUserState(response.user);
      nonceNode.textContent = String(response.user.nonce);

      hashHost.replaceChildren(
        copyRow(
          'Empreinte de la graine serveur',
          response.user.serverSeedHash,
          'Nouvelle empreinte, nouveau compteur : la partie repart d’un engagement neuf.',
        ),
      );

      const revealed = el('div', { class: 'reveal' }, [
        el('p', { class: 'reveal__lead' }, [
          'Cette graine a servi à tes ',
          el('strong', { text: String(response.revealed.nonceReached) }),
          ' derniers tirages : son empreinte était publiée avant, donc si sha256 de la ',
          'graine révélée redonne bien cette empreinte, aucun résultat n’a pu être ',
          'modifié après coup.',
        ]),
        copyRow(
          'Graine serveur révélée',
          response.revealed.serverSeed,
          'À comparer avec l’empreinte ci-dessous.',
        ),
        copyRow(
          'Empreinte annoncée à l’époque',
          response.revealed.serverSeedHash,
          'sha256(graine révélée) doit être exactement cette valeur.',
        ),
      ]);

      revealHost.replaceChildren(revealed);
      enter(revealed);
      audio.play('win');
      toast('Ancienne graine révélée.', { kind: 'success', sound: false });
    } catch (error) {
      toast(error instanceof ApiError ? error.userMessage : 'Rotation impossible.', {
        kind: 'error',
      });
    } finally {
      rotateButton.disabled = false;
      rotateButton.textContent = 'Changer de graine';
    }
  }

  sheet.body.appendChild(
    el('div', { class: 'stack' }, [
      el('div', { class: 'group' }, [
        el('h3', { class: 'group__title', text: 'Engagement du serveur' }),
        hashHost,
      ]),

      el('div', { class: 'group' }, [
        el('h3', { class: 'group__title', text: 'Ta graine' }),
        el('p', { class: 'note', text: `Ton grain de sel personnel, ${CLIENT_SEED_MAX_LENGTH} caractères au maximum. Change-la quand tu veux.` }),
        el('div', { class: 'row' }, [seedInput, saveButton]),
      ]),

      el('div', { class: 'group' }, [
        el('h3', { class: 'group__title', text: 'Compteur' }),
        el('div', { class: 'stat' }, [
          el('span', { class: 'stat__label', text: 'Nonce' }),
          nonceNode,
        ]),
        el('p', {
          class: 'note',
          text: 'Nombre de tirages déjà résolus avec la graine serveur en cours. Il repart à zéro à chaque rotation.',
        }),
      ]),

      el('div', { class: 'group' }, [
        el('h3', { class: 'group__title', text: 'Rotation' }),
        el('p', {
          class: 'note',
          text: 'Change la graine serveur : l’ancienne est alors révélée, ce qui te permet de vérifier a posteriori tous les tirages qu’elle a produits.',
        }),
        rotateButton,
        revealHost,
      ]),
    ]),
  );
}
